import { ReactNode, useState } from 'react';
import { Link } from '@tanstack/react-router';

import { BoardThemes, ChessBoard } from '@exports/ChessBoard';
import { useBoardTheme } from '@exports/hooks/useBoardTheme';
import { cx } from '@libs/classnames';
import { formatDate } from '@libs/dates';
import { formatPossibleAnonymousName } from '@libs/formatNames';
import { Loader } from '@ui/components/Loader';
import { SlimArrowButton } from '@ui/components/SlimArrowButton';
import { BinIcon } from '@ui/icons/BinIcon';

import { SiteLayout } from '@features/shared/ui/SiteLayout';

import styles from './FriendMyGamesPage.module.css';

export type IInviteInfo = {
  specs: string[];
  game: IGame;
  created: number;
  canDelete: boolean;
};

export type IPlayer = {
  id: string;
  firstName: string;
  lastName: string;
};

export type IGame = {
  gameId: string;
  currentFen: string;
  white?: IPlayer;
  black?: IPlayer;
};

export function FriendMyGamesPage({
  logo,
  actions,
  isLoading,
  playerId,
  games,
  backUrl,
  onDelete,
}: {
  logo: ReactNode;
  actions: ReactNode;
  isLoading?: boolean;
  playerId?: string;
  games?: IInviteInfo[];
  backUrl?: string;
  onDelete?: (inviteId: string) => Promise<unknown>;
}) {
  const { boardTheme } = useBoardTheme();

  return (
    <SiteLayout siteLogo={logo} actions={actions}>
      <h1 className={styles.title}>
        <Link to="/game/friend" className={styles.desktopBackBtn}>
          <SlimArrowButton direction="left" size="32" colorPreset="currentColor" />
        </Link>
        Мои игры
      </h1>

      {isLoading && <Loader />}
      {!isLoading && (
        <section className={styles.games}>
          {games?.map((g) => (
            <Board
              key={g.game.gameId}
              game={g}
              theme={boardTheme}
              playerId={playerId}
              backUrl={backUrl}
              onDelete={onDelete}
            />
          ))}
        </section>
      )}
      {!isLoading && !games?.length && <center>Нет активных игр</center>}
    </SiteLayout>
  );
}

function Board({
  game,
  theme,
  playerId,
  backUrl,
  onDelete,
}: {
  game: IInviteInfo;
  playerId?: string;
  theme: BoardThemes;
  backUrl?: string;
  onDelete?: (inviteId: string) => Promise<unknown>;
}) {
  const isPlayerWhite = playerId === game.game.white?.id;
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = () => {
    setIsDeleting(true);
    onDelete?.(game.game.gameId);
  };

  return (
    <article className={cx(styles.item, { [styles.can_delete]: game.canDelete })}>
      <h1>
        {game.canDelete && (
          <button className={styles.item__delete} disabled={isDeleting} onClick={handleDelete}>
            <BinIcon />
          </button>
        )}
        {game.specs.join(', ')}
        <span className={styles.item__time}>
          {formatDate(game.created, { showTime: true, shortMonth: true })}
        </span>
      </h1>
      <div className={styles.item__board}>
        <Player player={isPlayerWhite ? game.game.black : game.game.white} />
        <ChessBoard
          className={styles.item__board__board}
          orientation={isPlayerWhite ? 'white' : 'black'}
          position={game.game.currentFen}
          notation="none"
          theme={theme}
          noShadow
        />
        <Player player={isPlayerWhite ? game.game.white : game.game.black} />
      </div>
      <Link
        to="/game/friend/$gameId"
        params={{ gameId: game.game.gameId }}
        state={{ backUrl }}
        className={styles.link}
      />
    </article>
  );
}

function Player({ player }: { player?: IPlayer }) {
  return (
    <div className={styles.item__board__player}>
      {player ? formatPossibleAnonymousName(player) : '—'}
    </div>
  );
}
